import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { syncTokenToSW } from "@/lib/periodicSync";
import { getDeviceId, getDeviceName } from "@/lib/device";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * AuthCallback — handles the redirect back from Emergent Google Auth.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Emergent lands the user at `<our origin>/auth/callback#session_id=<id>`.
 * We extract the session_id from the URL fragment (NOT query string), call our
 * own backend endpoint to exchange it for our app JWT (carrying the `dsid`
 * claim so the one-device-login enforcement keeps working), store the token +
 * user in localStorage, then redirect to /student.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // useRef guard prevents React StrictMode double-invocation from
    // trying to exchange the session_id twice (which would 401 the second call)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("Missing session id. Please try signing in again.");
      setTimeout(() => nav("/login", { replace: true }), 1500);
      return;
    }
    const sid = decodeURIComponent(m[1]);

    (async () => {
      try {
        const r = await api.post("/auth/google/session", {
          session_id: sid,
          device_id: getDeviceId(),
          device_name: getDeviceName(),
        });
        const { token, user, replaced_previous_device, is_new_user, profile_completion } = r.data;
        localStorage.setItem("pm_token", token);
        localStorage.setItem("pm_user", JSON.stringify(user));
        try { await syncTokenToSW(token); } catch (e) { /* noop */ }
        setUser(user);
        // Clear the hash so a refresh doesn't try to re-process the (now used) session_id
        window.history.replaceState(null, "", "/student");
        if (replaced_previous_device) {
          toast.success("Signed in. Your previous device was logged out.", { duration: 5000 });
        } else if (is_new_user) {
          toast.success("Welcome to PrepMaster! Pick a course to start reviewing.", { duration: 5000 });
        } else {
          toast.success("Signed in successfully");
        }
        // New students land on the Enroll Course page so they can pick a
        // course immediately. Returning students go to their dashboard.
        const target = (is_new_user || (profile_completion && !profile_completion.is_complete))
          ? "/student/enrollments"
          : "/student";
        nav(target, { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed. Please try again.";
        setError(msg);
        setTimeout(() => nav("/login", { replace: true }), 2000);
      }
    })();
  }, [nav, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="text-center" data-testid="auth-callback-screen">
        {error ? (
          <>
            <div className="text-rose-700 dark:text-rose-300 font-semibold">{error}</div>
            <div className="text-sm text-slate-500 mt-1">Redirecting back to login…</div>
          </>
        ) : (
          <>
            <Loader2 className="w-7 h-7 animate-spin mx-auto text-blue-600 mb-3" />
            <div className="text-slate-700 dark:text-slate-200 font-display font-semibold">Finishing sign-in…</div>
            <div className="text-xs text-slate-500 mt-1">Please wait a moment.</div>
          </>
        )}
      </div>
    </div>
  );
}
